import type { ReactNode } from 'react';

import { FieldHelp } from '../FieldHelp';
import styles from './Form.module.css';

export interface FormLineProps {
  children: ReactNode;
  className?: string;
}

/** 表单行内的一行控件：横向铺开、超出换行。对应页面上原先手写（且类名未定义）的 inlineRow。 */
export function FormLine({ children, className }: FormLineProps) {
  return <div className={[styles.line, className].filter(Boolean).join(' ')}>{children}</div>;
}

export interface FormRowProps {
  children: ReactNode;
  className?: string;
  /** 错误态：提示行转为危险色。 */
  error?: boolean;
  /** 标签后的问号说明文案，配合 `helpLabel` 一起给。 */
  help?: ReactNode;
  /** 问号按钮的可访问名称，如「活动名称说明」。 */
  helpLabel?: string;
  /** 控件下方的提示或错误文案。 */
  hint?: ReactNode;
  label: ReactNode;
  /** 标签栏宽度，默认 150px（与 Figma 表单一致）。 */
  labelWidth?: number;
  /**
   * `inline` 单行控件横向铺开（自动包一层 `FormLine`）；
   * `stack` 多行控件纵向排列，由调用方用 `FormLine` 组织每一行。
   */
  layout?: 'inline' | 'stack';
  /** 必填标记，渲染为标签前的红色星号。 */
  required?: boolean;
}

/**
 * 表单行：左侧标签、右侧控件、下方提示。
 *
 * 只负责版式与标记，不碰值与校验；标签用 `div` 而不是 `label`，
 * 与既有页面一致——字段的可访问名称由控件自身的 `ariaLabel` 提供，
 * 换成 `label htmlFor` 会引入另一套关联语义、并与现有断言冲突。
 */
export function FormRow({ children, className, error = false, help, helpLabel, hint, label, labelWidth, layout = 'inline', required = false }: FormRowProps) {
  return <div className={[styles.root, className].filter(Boolean).join(' ')} data-error={error || undefined}>
    <div className={[styles.label, required ? styles.required : null].filter(Boolean).join(' ')} style={labelWidth === undefined ? undefined : { flexBasis: `${labelWidth}px` }}>
      {label}
      {help && helpLabel ? <FieldHelp content={help} label={helpLabel} /> : null}
    </div>
    <div className={styles.body}>
      {layout === 'inline' ? <FormLine>{children}</FormLine> : children}
      {hint ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  </div>;
}

export interface FormSectionProps {
  /** 标题右侧的徽标，如「必填」。 */
  badge?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** 标题后的问号说明文案，配合 `helpLabel` 一起给。 */
  help?: ReactNode;
  /** 问号按钮的可访问名称。 */
  helpLabel?: string;
  /** 是否在标题上方画一条分隔线（独立设置块用它代替纯标题）。 */
  divider?: boolean;
  title: ReactNode;
}

/** 表单分节标题：顶部分隔线可选，标题可带徽标与问号说明。 */
export function FormSection({ badge, children, className, divider = false, help, helpLabel, title }: FormSectionProps) {
  return <section className={[styles.section, className].filter(Boolean).join(' ')} data-divider={divider || undefined}>
    <h3 className={styles.sectionTitle}>
      <span className={styles.sectionTitleRow}>
        {title}
        {badge}
        {help && helpLabel ? <FieldHelp content={help} label={helpLabel} /> : null}
      </span>
    </h3>
    {children}
  </section>;
}
